import React, { useState, useEffect, Suspense } from "react";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BillingProvider, useBilling } from "./context/BillingContext";
import { PaywallProvider } from "./context/PaywallContext";
import { FeatureFlagProvider } from "./context/FeatureFlagContext";
import { captureFbclid } from "./utils/metaPixel";
import { projectTabPath } from "./lib/projectTabs";

// ─── BB-080 Phase 1: Code-splitting ──────────────────────────────────────────
// Critical visitor path (LandingPage, RegisterPage, LoginPage) stays in the
// main bundle so first-paint is instant. Everything else is lazy-loaded —
// chunks are fetched only when the user navigates to that route.
//
// This reduces the initial JS payload from ~1.67 MB → ~200-300 KB, cutting
// first-paint time from ~9s to ~1-2s for new visitors on cold cache.
// ─────────────────────────────────────────────────────────────────────────────

// ── Main bundle: critical visitor path (no lazy) ──────────────────────────────
import LandingPage from "./pages/LandingPage";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";

// ── Lazy-loaded: authenticated app routes (largest chunks) ────────────────────
const DashboardPage = lazyWithRetry(() => import("./pages/DashboardPage"), "DashboardPage");
const ProjectPage = lazyWithRetry(() => import("./pages/ProjectPage"), "ProjectPage");
const WizardPage = lazyWithRetry(() => import("./pages/WizardPage"), "WizardPage");
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"), "SettingsPage");
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"), "AdminPage");
const OnboardingPage = lazyWithRetry(() => import("./pages/OnboardingPage"), "OnboardingPage");
const FinishSetupPage = lazyWithRetry(() => import("./pages/FinishSetupPage"), "FinishSetupPage");
const WelcomePage = lazyWithRetry(() => import("./pages/WelcomePage"), "WelcomePage");
const PricingPage = lazyWithRetry(() => import("./pages/PricingPage"), "PricingPage");

// ── Lazy-loaded: public info pages (small but no reason to block initial load) ─
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"), "ForgotPasswordPage");
const Verify2FAPage = lazyWithRetry(() => import("./pages/Verify2FAPage"), "Verify2FAPage");
const ChangelogPage = lazyWithRetry(() => import("./pages/ChangelogPage"), "ChangelogPage");
const HelpPage = lazyWithRetry(() => import("./pages/HelpPage"), "HelpPage");
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"), "PrivacyPage");
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"), "TermsPage");
const RefundPolicyPage = lazyWithRetry(() => import("./pages/RefundPolicyPage"), "RefundPolicyPage");
const ContractorLandingPage = lazyWithRetry(() => import("./pages/ContractorLandingPage"), "ContractorLandingPage");
// R398: SendEmailPage removed — internal test tool with PII, not for production
const CapturePage = lazyWithRetry(() => import("./pages/CapturePage"), "CapturePage");
const InvitePage = lazyWithRetry(() => import("./pages/InvitePage"), "InvitePage");  // BB-002 Phase 4
// R3 PR CAL: the /health-check booking page and the /funnel/* trial pages were
// removed (booking paused, guided trial ended Oct 23, 2026); old links redirect.
const FunnelLeadsAdminPage = lazyWithRetry(() => import("./pages/FunnelLeadsAdminPage"), "FunnelLeadsAdminPage");

// ── Lazy-loaded: heavy authenticated components ───────────────────────────────
const BugReportButton = lazyWithRetry(() => import("./components/BugReportButton"), "BugReportButton");
const StratalogyAgent = lazyWithRetry(() => import("./components/StratalogyAgent"), "StratalogyAgent");

// AgentProvider is a lightweight context wrapper — keep it eager so it can
// wrap routes without Suspense blocking. The heavy StratalogyAgent is lazy above.
import { AgentProvider } from "./context/AgentContext";
import BillingWarningBanner from "./components/BillingWarningBanner";
import AppErrorBoundary from "./components/AppErrorBoundary";
import GlobalDropZone from "./components/GlobalDropZone";

// R4 PR 1: reset the error boundary on navigation by keying it on the path.
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <AppErrorBoundary key={pathname}>{children}</AppErrorBoundary>;
}

// R3 QA T2-2: old and path-style project links go to the canonical
// /project/:projectId?tab=<tab> URL (bare URL for an invalid tab).
function ProjectTabRedirect() {
  const { projectId, tab } = useParams();
  return <Navigate to={projectTabPath(projectId, tab)} replace />;
}

function ProjectsAliasRedirect() {
  const { projectId } = useParams();
  return <Navigate to={projectTabPath(projectId)} replace />;
}

// Capture fbclid from Meta ad click URLs on first page load.
// Stores it as the _fbc cookie so the Conversions API can match
// ad clicks to server-side events (100% improvement in reported conversions).
captureFbclid();

// ── BB-080: Lightweight suspense fallback for lazy-loaded route chunks ────────
// Minimal spinner that matches the app's visual style. Shown only briefly while
// a lazy chunk downloads (typically <500ms on broadband). Much lighter than the
// full LoadingScreen which is reserved for server-wake scenarios.
function ChunkFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center space-y-3">
        <div className="w-8 h-8 border-2 border-[#359b95] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-slate-400 text-sm">Loading…</p>
      </div>
    </div>
  );
}

// Shown while auth state is being checked.
// Uses healthMs from AuthContext to show accurate server-wake progress.
function LoadingScreen() {
  const { healthMs } = useAuth();
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // healthMs > 0 means we're actively polling /api/health (server is cold/waking)
  const isWakingServer = healthMs > 0;
  const wakeSeconds = Math.floor(healthMs / 1000);

  // Progress bar: 0–100% over 90 seconds of health polling
  const MAX_WAIT_S = 90;
  const wakeProgress = isWakingServer
    ? Math.min(Math.round((wakeSeconds / MAX_WAIT_S) * 100), 95)
    : 0;

  // Message logic
  let message;
  if (isWakingServer) {
    message =
      wakeSeconds < 15
        ? "Server is starting up…"
        : wakeSeconds < 40
          ? "Almost ready — server is warming up…"
          : "Still starting up — this can take up to 60 seconds after a deploy…";
  } else {
    message =
      seconds < 4
        ? "Loading Stratalogy…"
        : seconds < 8
          ? "Connecting to server…"
          : "Verifying your session…";
  }

  const showReload = isWakingServer ? wakeSeconds >= 50 : seconds >= 20;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center space-y-5 max-w-sm mx-auto px-6">
        {/* Spinner */}
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />

        {/* Message */}
        <p className="text-slate-300 text-sm font-medium">{message}</p>

        {/* Progress bar — only shown while waking server */}
        {isWakingServer && (
          <div className="space-y-2">
            <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-1.5 bg-blue-500 rounded-full transition-all duration-1000"
                style={{ width: `${wakeProgress}%` }}
              />
            </div>
            <p className="text-slate-500 text-xs">
              Waiting for server… {wakeSeconds}s
            </p>
          </div>
        )}

        {/* Explanation + reload */}
        {showReload && (
          <div className="space-y-3 pt-1">
            <p className="text-slate-500 text-xs leading-relaxed">
              {isWakingServer
                ? "The server wakes up automatically after a period of inactivity. It retries every few seconds — no action needed."
                : "The server may be starting up. It will retry automatically."}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors">
              Reload page
            </button>
          </div>
        )}

        {/* Reassurance note during early wait */}
        {isWakingServer && wakeSeconds < 50 && (
          <p className="text-slate-600 text-xs">
            Retrying automatically every 5 seconds…
          </p>
        )}
      </div>
    </div>
  );
}

// Requires login; if logged in but onboarding not done → send to /onboarding
// Exception: /onboarding itself is allowed through so the user can complete it
const CARD_TRIAL_OPEN = ["/onboarding", "/finish-setup", "/welcome", "/pricing", "/settings"];
const needsAccountType = (u) => !!u && !u.is_demo && !String(u.account_type || "").trim();

function PrivateRoute({ children, requireOnboarding = true }) {
  const { user, loading } = useAuth();
  const { billing, billingLoading } = useBilling();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user)
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  // Keep is_admin on /admin. Empty account_type is the only reason anyone else is sent to /onboarding.
  const adminPage =
    user.is_admin === true &&
    (location.pathname === "/admin" || location.pathname.startsWith("/admin/"));
  if (needsAccountType(user) && location.pathname !== "/onboarding" && !adminPage) {
    return (
      <Navigate
        to="/onboarding"
        replace
      />
    );
  }
  if (requireOnboarding && !user.onboarding_complete && !adminPage) {
    return (
      <Navigate
        to="/onboarding"
        replace
      />
    );
  }
  if (user.card_trial === true) {
    if (!user.account_type && location.pathname !== "/onboarding") {
      return (
        <Navigate
          to="/onboarding"
          replace
        />
      );
    }
    if (!CARD_TRIAL_OPEN.includes(location.pathname)) {
      if (billingLoading) return <LoadingScreen />;
      if (billing?.is_active !== true) {
        return (
          <Navigate
            to="/finish-setup"
            replace
          />
        );
      }
    }
  }
  return children;
}

// Only render the floating bug report button when a user is logged in
function BugReportButtonWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Suspense fallback={null}>
      <BugReportButton />
    </Suspense>
  );
}

// Only render the Stratalogy Agent for authenticated users who completed onboarding
function AgentWrapper() {
  const { user } = useAuth();
  if (!user || !user.onboarding_complete) return null;
  return (
    <Suspense fallback={null}>
      <StratalogyAgent />
    </Suspense>
  );
}

// Public routes redirect logged-in users who have finished onboarding to dashboard,
// and logged-in users who haven't to onboarding
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return children;
  return !user.onboarding_complete || needsAccountType(user) ? (
    <Navigate
      to="/onboarding"
      replace
    />
  ) : (
    <Navigate
      to="/dashboard"
      replace
    />
  );
}

// Root route: logged-out → LandingPage, logged-in → dashboard
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <LandingPage />;
  if (!user.onboarding_complete || needsAccountType(user))
    return (
      <Navigate
        to="/onboarding"
        replace
      />
    );
  return (
    <Navigate
      to="/dashboard"
      replace
    />
  );
}

export default function App() {
  return (
    <FeatureFlagProvider>
    <AuthProvider>
      <BrowserRouter>
        <GlobalDropZone />
        <BillingProvider>
          <PaywallProvider>
            <AgentProvider>
            <Toaster
              position="top-right"
              richColors
              closeButton
            />
            <BillingWarningBanner />
            <AppErrorBoundary silent>
            <BugReportButtonWrapper />
            </AppErrorBoundary>
            <AppErrorBoundary silent>
            <AgentWrapper />
            </AppErrorBoundary>
            <Suspense fallback={<ChunkFallback />}>
            <RouteErrorBoundary>
            <Routes>
              {/* ── Critical visitor path (in main bundle, no lazy) ── */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />

              {/* Root — landing page for visitors, dashboard redirect for logged-in */}
              <Route
                path="/"
                element={<RootRoute />}
              />

              {/* ── Lazy-loaded: auth-related routes ── */}
              <Route
                path="/forgot-password"
                element={<ForgotPasswordPage />}
              />
              <Route
                path="/verify-2fa"
                element={<Verify2FAPage />}
              />

              {/* Onboarding — requires login but NOT completed onboarding */}
              <Route
                path="/onboarding"
                element={
                  <PrivateRoute requireOnboarding={false}>
                    <OnboardingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/finish-setup"
                element={
                  <PrivateRoute requireOnboarding={false}>
                    <FinishSetupPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/welcome"
                element={
                  <PrivateRoute requireOnboarding={false}>
                    <WelcomePage />
                  </PrivateRoute>
                }
              />

              {/* ── Lazy-loaded: authenticated app routes ── */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/wizard"
                element={
                  <PrivateRoute>
                    <WizardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/project/:projectId"
                element={
                  <PrivateRoute>
                    <ProjectPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/project/:projectId/:tab"
                element={
                  <PrivateRoute>
                    <ProjectTabRedirect />
                  </PrivateRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <PrivateRoute>
                    <ProjectsAliasRedirect />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pricing"
                element={<PricingPage />}
              />

              {/* Admin — requires login + is_admin flag */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute>
                    <AdminPage />
                  </PrivateRoute>
                }
              />

              {/* ── Contractor landing page (dedicated, ad-targetable) ── */}
              <Route
                path="/contractors"
                element={<ContractorLandingPage />}
              />

              {/* ── Lazy-loaded: public info pages ── */}
              <Route
                path="/changelog"
                element={<ChangelogPage />}
              />
              <Route
                path="/help"
                element={<HelpPage />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPage />}
              />
              <Route
                path="/terms"
                element={<TermsPage />}
              />
              <Route
                path="/refund-policy"
                element={<RefundPolicyPage />}
              />
              {/* R398: /send-email removed — internal test tool with PII */}

              {/* Mobile camera capture — public, code-authenticated */}
              <Route
                path="/capture/:code"
                element={<CapturePage />}
              />

              {/* BB-002 Phase 4: Project invite acceptance page */}
              <Route
                path="/invite/:inviteId"
                element={<InvitePage />}
              />

              {/* R3 PR CAL: booking paused and guided trial ended; old links redirect */}
              <Route path="/health-check" element={<Navigate to="/" replace />} />
              <Route path="/funnel/trial-success" element={<Navigate to="/login" replace />} />
              <Route path="/funnel/founder-success" element={<Navigate to="/login" replace />} />
              <Route path="/funnel/trial-cancel" element={<Navigate to="/" replace />} />
              <Route path="/funnel/founder-cancel" element={<Navigate to="/" replace />} />
              <Route
                path="/admin/funnel-leads"
                element={
                  <PrivateRoute>
                    <FunnelLeadsAdminPage />
                  </PrivateRoute>
                }
              />

              {/* Catch-all */}
              <Route
                path="*"
                element={
                  <Navigate
                    to="/"
                    replace
                  />
                }
              />
            </Routes>
            </RouteErrorBoundary>
            </Suspense>
          </AgentProvider>
          </PaywallProvider>
        </BillingProvider>
      </BrowserRouter>
    </AuthProvider>
    </FeatureFlagProvider>
  );
}
